import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { useTranslation } from 'react-i18next';
import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import { IncidenceTableDto } from 'apps/marsinet/src/app/typescript-axios';
import { GetIncidencesQueryDtoStatusEnum } from '../maintenance-orders/maintenance-order-list';

interface AssignIncidentToOmModalProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  maintenanceOrderId: string;
  deviceSerial?: string | number | null;
  onAssignedSuccess?: (maintenanceOrderId: string) => void | Promise<void>;
}

type IncidenceOption = {
  label: string;
  value: string;
};

const normalizeSerial = (value?: string | number | null): string => {
  if (value === undefined || value === null || value === 0) return '';

  return String(value).trim().toLowerCase();
};

const AssignIncidentToOmModal: React.FC<AssignIncidentToOmModalProps> = ({
  visible,
  setVisible,
  maintenanceOrderId,
  deviceSerial,
  onAssignedSuccess,
}) => {
  const { t } = useTranslation();

  const [incidences, setIncidences] = useState<IncidenceOption[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [missingSerial, setMissingSerial] = useState(false);

  useEffect(() => {
    if (!visible || !maintenanceOrderId) return;

    let cancelled = false;

    const loadIncidences = async (): Promise<void> => {
      setLoading(true);
      setMissingSerial(false);

      try {
        const [allIncidencesResponse, assignedResponse, orderSerial] =
          await Promise.all([
            apiServices.incidenceApi.incidenceControllerGetAllIncidences(GetIncidencesQueryDtoStatusEnum.Open),
            apiServices.incidenceApi.incidenceControllerGetIncidencesByMaintenanceOrder(
              maintenanceOrderId
            ),
            normalizeSerial(deviceSerial)
              ? Promise.resolve(deviceSerial)
              : apiServices.maintenanceOrderApi
                .maintenanceOrdersControllerGetMaintenanceOrderDetailsById(
                  maintenanceOrderId
                )
                .then(
                  (response) => response.data?.associated_component?.serial
                ),
          ]);

        if (cancelled) return;

        const targetSerial = normalizeSerial(orderSerial);

        if (!targetSerial) {
          setMissingSerial(true);
          setIncidences([]);
          return;
        }

        const assignedIds = new Set(
          (assignedResponse.data ?? [])
            .map((incidence) => incidence.id)
            .filter(Boolean)
        );

        const options = (allIncidencesResponse.data ?? [])
          .filter(
            (incidence: IncidenceTableDto) =>
              incidence.id &&
              !assignedIds.has(incidence.id) &&
              normalizeSerial(incidence.device) === targetSerial
          )
          .map((incidence: IncidenceTableDto) => ({
            label: incidence.incident_code
              ? `${incidence.incident_code}${incidence.description ? ` — ${incidence.description}` : ''
              }`
              : incidence.description ?? incidence.id,
            value: incidence.id,
          }));

        setIncidences(options);
      } catch (error) {
        console.error('Error loading incidences:', error);
        if (!cancelled) setIncidences([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadIncidences();

    return () => {
      cancelled = true;
    };
  }, [visible, maintenanceOrderId, deviceSerial]);

  const emptyMessage = useMemo(() => {
    if (loading) return t('loading');
    if (missingSerial) return t('pages.incidents.assignIncidentToOm.no_device');

    return t('pages.incidents.assignIncidentToOm.none_available');
  }, [loading, missingSerial, t]);

  const onHide = (): void => {
    setSelectedIds([]);
    setIncidences([]);
    setVisible(false);
  };

  const onSubmit = async (): Promise<void> => {
    if (selectedIds.length === 0) return;

    setSaving(true);

    try {
      await apiServices.maintenanceOrderApi.maintenanceOrdersControllerAssignIncidences(
        selectedIds,
        maintenanceOrderId
      );

      await onAssignedSuccess?.(maintenanceOrderId);
      onHide();
    } catch (error) {
      console.error('Error associating incidences:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={<h2>{t('pages.incidents.assignIncidentToOm.title')}</h2>}
      style={{ width: '40rem' }}
      breakpoints={{ '960px': '80vw', '640px': '95vw' }}
    >
      <label htmlFor="assign-incident-id" className="block mb-2">
        {t('pages.incidents.assignIncidentToOm.incident_label')}
        <span className="text-red-500 ml-1" aria-hidden="true">
          *
        </span>
      </label>

      <MultiSelect
        id="assign-incident-id"
        className="w-full"
        value={selectedIds}
        options={incidences}
        onChange={(e) => setSelectedIds(e.value as string[])}
        optionLabel="label"
        optionValue="value"
        filter
        display="chip"
        disabled={loading || missingSerial}
        placeholder={
          loading
            ? t('loading')
            : t('pages.incidents.assignIncidentToOm.search_incident_placeholder')
        }
        emptyMessage={emptyMessage}
        emptyFilterMessage={t('noResults')}
        itemTemplate={(option: IncidenceOption) => (
          <div
            style={{
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {option.label}
          </div>
        )}
      />

      <small className="block mt-2 text-600">
        {t('pages.incidents.assignIncidentToOm.same_device_hint')}
      </small>

      <div className="flex align-items-center justify-content-end gap-2 mt-4">
        <Button
          id="button-assign-incident-to-om"
          severity="secondary"
          icon="pi pi-save"
          label={t('save')}
          className="p-button-raised"
          type="button"
          onClick={onSubmit}
          loading={saving}
          disabled={selectedIds.length === 0}
        />
      </div>
    </Dialog>
  );
};

export default AssignIncidentToOmModal;